import { FormControl, FormHelperText, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { ToolTip } from 'components/Tooltip/ToolTip';
import { FC, useState } from 'react';
import Select, { components as selectComponents, InputProps as SelectInputProps } from 'react-select';
import { validateInput } from '../../utils/validators/Validators';
import { FormValidators } from '../Form';
import './styles.scss';

/** Prevent browser password/email autofill from hijacking searchable selects (e.g. org switcher). */
const AutofillSafeInput = (props: SelectInputProps) => (
  <selectComponents.Input
    {...props}
    // Non-standard token: browsers often ignore plain "off" on searchable selects.
    autoComplete="one-time-code"
    autoCorrect="off"
    autoCapitalize="none"
    spellCheck="false"
    role="combobox"
  />
);

interface OwnProps {
  field?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  typeValue?: string;
  onChange: (value: any, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  isSearchable?: boolean;
  label?: string;
  disableFuture?: boolean;
  labelId?: string;
  inputValue: any;
  options: any[];
  inputProps?: any;
  className?: string;
  style?: any;
  isMulti?: boolean;
  isClearable?: boolean;
  id: any;
  showSelectAll?: boolean;
  controlHeight?: number;
}
const MultiSelect: FC<OwnProps> = ({
  hasError,
  validators,
  onChange,
  field,
  inputValue,
  options,
  fieldError,
  disabled,
  className,
  label,
  id,
  style,
  placeholder,
  isClearable = true,
  isSearchable = true,
  isMulti,
  showSelectAll = false,
  controlHeight = 37,
}) => {
  const theme = useTheme();
  const [error, setError] = useState<
    | {
        error: boolean;
        message: string;
      }
    | undefined
  >(undefined);
  const handleChange = (selectedOption: any) => {
    const inputError = validateInput(validators, selectedOption);
    setError(inputError);
    onChange(selectedOption, field || '', inputError);
  };

  const isAllSelected = inputValue && inputValue?.length === options?.length && options?.length > 0;

  const enhancedOptions =
    showSelectAll && isMulti && options.length > 0
      ? [
          {
            value: 'select-all',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px 0',
                  width: '100%',
                }}
                onClick={e => {
                  e.stopPropagation();
                  if (isAllSelected) {
                    handleChange([]);
                  } else {
                    handleChange(options);
                  }
                }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={e => {
                    e.stopPropagation();
                    if (isAllSelected) {
                      handleChange([]);
                    } else {
                      handleChange(options);
                    }
                  }}
                  style={{ margin: 0 }}
                />
                <span
                  style={{
                    fontSize: '14px',
                    color: '#333',
                    fontWeight: 'normal',
                  }}>
                  Select All
                </span>
              </div>
            ),
            isDisabled: true,
          },
          ...options,
        ]
      : options;

  const handleOptionChange = (selectedOption: any) => {
    if (!selectedOption) {
      const inputError = validateInput(validators, '');
      setError(inputError);
      onChange('', field || '', inputError);
      return;
    }

    if (selectedOption && Array.isArray(selectedOption)) {
      const filteredOptions = selectedOption.filter((option: any) => option.value !== 'select-all');
      let inputError: { error: boolean; message: string } | undefined = undefined;
      if (validators && validators.length > 0) {
        const mandatoryValidator = validators.find(v => v.check.name === 'validateMandatoryField');
        if (mandatoryValidator && filteredOptions.length === 0) {
          inputError = { error: true, message: mandatoryValidator.message || strings.requiredText };
        }
      }
      setError(inputError);
      onChange(filteredOptions, field || '', inputError);
    } else if (selectedOption && selectedOption.value !== 'select-all') {
      const inputError = validateInput(validators, selectedOption);
      setError(inputError);
      onChange(selectedOption, field || '', inputError);
    }
  };
  const customMessage = () => (!error && hasError && fieldError ? strings.requiredText : '');
  const helperText = error && error.error ? error.message : customMessage();
  const hasValue = isMulti ? Array.isArray(inputValue) && inputValue.length > 0 : inputValue !== '' && inputValue != null;

  return (
    <FormControl
      margin="none"
      sx={{
        verticalAlign: 'top',
        '.select': {
          fontFamily: theme.typography.fontFamily,
        },
      }}
      error={!!((error && error.error) || (!error && hasError && fieldError))}
      className={`dropdown ${isMulti ? 'is-multi' : ''} ${className} ${
        (error && error.error) || (!error && hasError && fieldError) ? 'mainError' : ''
      }`}
      fullWidth>
      {hasValue && label ? (
        <ToolTip title={label}>
          <Typography variant="body2" className="labels" component="span" title={label}>
            {label}
          </Typography>
        </ToolTip>
      ) : null}

      <Select
        className="select"
        options={enhancedOptions}
        components={{ Input: AutofillSafeInput }}
        styles={{
          ...style,
          menu: (provided: any, state: any) => {
            const base = {
              ...provided,
              zIndex: 9999,
            };
            return typeof style?.menu === 'function'
              ? style.menu(base, state)
              : { ...base, ...(style?.menu || {}) };
          },
          menuPortal: (provided: any, state: any) => {
            const base = {
              ...provided,
              zIndex: 9999,
            };
            return typeof style?.menuPortal === 'function'
              ? style.menuPortal(base, state)
              : { ...base, ...(style?.menuPortal || {}) };
          },
          menuList: (provided: any, state: any) => {
            const base = { ...provided };
            return typeof style?.menuList === 'function'
              ? style.menuList(base, state)
              : { ...base, ...(style?.menuList || {}) };
          },
          control: (provided: any, state: any) => {
            const base = {
              ...provided,
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 6,
              boxShadow: 'none',
              minHeight: controlHeight,
              height: isMulti ? 'auto' : controlHeight,
              fontSize: '0.875rem',
              alignItems: 'center',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
            };
            return typeof style?.control === 'function'
              ? style.control(base, state)
              : { ...base, ...(style?.control || {}) };
          },
          valueContainer: (provided: any, state: any) => {
            const base = {
              ...provided,
              padding: isMulti ? '4px 6px' : '0 8px',
              ...(isMulti
                ? { gap: 0 }
                : { height: '100%', display: 'flex', alignItems: 'center' }),
            };
            return typeof style?.valueContainer === 'function'
              ? style.valueContainer(base, state)
              : { ...base, ...(style?.valueContainer || {}) };
          },
          singleValue: (provided: any, state: any) => {
            const base = {
              ...provided,
              fontSize: '0.875rem',
              lineHeight: '1.4375em',
              margin: 0,
            };
            return typeof style?.singleValue === 'function'
              ? style.singleValue(base, state)
              : { ...base, ...(style?.singleValue || {}) };
          },
          indicatorsContainer: (provided: any, state: any) => {
            const base = {
              ...provided,
              height: isMulti ? undefined : controlHeight,
              alignItems: 'center',
              '& > div': {
                padding: '0 6px',
              },
            };
            return typeof style?.indicatorsContainer === 'function'
              ? style.indicatorsContainer(base, state)
              : { ...base, ...(style?.indicatorsContainer || {}) };
          },
          placeholder: (provided: any) => ({
            ...provided,
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.6)',
            margin: 0,
          }),
          input: (provided: any) => ({
            ...provided,
            fontSize: '0.875rem',
            margin: 0,
            padding: 0,
          }),
          multiValue: (provided: any) => ({
            ...provided,
            fontSize: '0.75rem',
            margin: '1px 2px',
            height: 24,
            minHeight: 24,
          }),
          multiValueLabel: (provided: any) => ({
            ...provided,
            fontSize: '0.75rem',
            padding: '0 4px',
            paddingLeft: 6,
            lineHeight: '22px',
          }),
          multiValueRemove: (provided: any) => ({
            ...provided,
            padding: '0 4px',
          }),
          option: (provided: any, state: any) => {
            const base = {
              ...provided,
              fontSize: '0.875rem',
              padding: '8px 12px',
            };
            return typeof style?.option === 'function'
              ? style.option(base, state)
              : { ...base, ...(style?.option || {}) };
          },
          indicatorSeparator: (provided: any) => ({
            ...provided,
            display: 'none',
          }),
          clearIndicator: (provided: any) => ({
            ...provided,
            padding: 2,
          }),
          dropdownIndicator: (provided: any) => ({
            ...provided,
            padding: 2,
          }),
        }}
        isMulti={isMulti}
        isDisabled={disabled}
        isSearchable={isSearchable}
        id={id}
        inputId={id ? `${id}-input` : undefined}
        name={id ? `${id}-select` : undefined}
        isClearable={isClearable}
        value={inputValue}
        placeholder={label}
        closeMenuOnSelect={isMulti ? false : true}
        onChange={value => {
          handleOptionChange(value);
        }}
        menuPortalTarget={document.body}
      />
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};
export default MultiSelect;
